import React, { useState } from "react";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import {
  READ_FILE_TOOL_DESCRIPTION,
  READ_FILE_TOOL_NAME,
  useAgentModels,
} from "src/api/agents/management";
import { ToolCard } from "src/base-components/agents/ToolCard";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { Pill } from "src/base-components/Pill";
import { Tabs } from "src/base-components/Tabs";
import type {
  AgentModelInfo,
  AgentVersion,
  ToolType,
} from "src/clients/agent-management";
import { ModelIcon } from "src/genai/ModelSelector/ModelIcon";
import { WhitePane } from "src/jobs/common/WhitePane";
import type { JsonSchema } from "src/jsonSchema/types";
import { JSONSchemaRenderer } from "src/jsonSchema/viewer/JSONSchemaRenderer";

type Props = {
  version: AgentVersion;
  agentName: string;
  agentDescription: string;
};

export const AgentVersionReadOnlyView: React.FC<Props> = ({
  version,
  agentName,
  agentDescription,
}) => {
  const { configuration } = version;
  const tools = configuration.tools ?? [];
  const configurableTools = tools.filter((t) => t.type !== "read_file");
  const hasReadFileTool = tools.some((t) => t.type === "read_file");

  const { data: models } = useAgentModels();
  const modelInfo = models?.find(
    (m) => m.model_id === configuration.model_config.model,
  );

  const inputSchema = configuration.input_schema;
  const outputSchema = configuration.output_schema;
  const hasInputSchema =
    inputSchema &&
    inputSchema.properties &&
    Object.keys(inputSchema.properties).length > 0;
  const hasOutputSchema =
    outputSchema &&
    outputSchema.properties &&
    Object.keys(outputSchema.properties).length > 0;

  return (
    <div className="h-auto">
      <WhitePane noMinWidth>
        <div className="w-full">
          {/* Agent Header */}
          <div className="border-gray-200 border-b pt-5 pr-10 pb-6 pl-11">
            <h2 className="font-inter-semibold-16px text-gray-800">
              {agentName}
            </h2>
            <div className="mt-2">
              <Pill size="sm" variant="indigo-light">
                <Pill.Icon icon={faSparkles} />
                <Pill.Text>Pre-built by Taktile</Pill.Text>
              </Pill>
            </div>
          </div>

          {/* Content */}
          <div className="pt-9 pr-10 pb-11 pl-11">
            <OverviewSection
              agentDescription={agentDescription}
              modelDisplayName={
                modelInfo?.display_name ?? configuration.model_config.model
              }
              modelInfo={modelInfo}
            />

            <ToolsSection
              configurableTools={configurableTools}
              hasReadFileTool={hasReadFileTool}
            />

            {(hasInputSchema || hasOutputSchema) && (
              <SchemaSection
                hasInputSchema={!!hasInputSchema}
                hasOutputSchema={!!hasOutputSchema}
                inputSchema={inputSchema}
                outputSchema={outputSchema}
              />
            )}
          </div>
        </div>
      </WhitePane>
    </div>
  );
};

const OverviewSection: React.FC<{
  modelDisplayName: string;
  modelInfo: AgentModelInfo | undefined;
  agentDescription: string;
}> = ({ modelDisplayName, modelInfo, agentDescription }) => (
  <div className="mb-8">
    <div className="mb-5 flex items-center justify-between">
      <h3 className="font-inter-semibold-14px text-gray-800">Overview</h3>
      <div className="flex items-center gap-x-1.5 font-inter-normal-12px text-gray-800">
        {modelInfo && (
          <ModelIcon
            modelName={modelDisplayName}
            provider={modelInfo.provider}
          />
        )}
        <span>{modelDisplayName}</span>
      </div>
    </div>
    {agentDescription && (
      <div className="rounded-lg bg-gray-50 p-4">
        <MarkdownRenderer
          content={agentDescription}
          dataLoc="prebuilt-agent-description"
        />
      </div>
    )}
  </div>
);

const ToolsSection: React.FC<{
  configurableTools: NonNullable<AgentVersion["configuration"]["tools"]>;
  hasReadFileTool: boolean;
}> = ({ configurableTools, hasReadFileTool }) => (
  <div className="mb-8">
    <h3 className="mb-3 font-inter-semibold-14px text-gray-800">Tools</h3>
    <div className="rounded-lg bg-gray-50 p-4">
      <div className="grid grid-cols-2 gap-3">
        {hasReadFileTool && (
          <ToolCard
            dataLoc="readonly-tool-card-read-file"
            description={READ_FILE_TOOL_DESCRIPTION}
            name={READ_FILE_TOOL_NAME}
            type={"read_file" as ToolType}
          />
        )}
        {configurableTools.map((tool) => (
          <ToolCard
            key={tool.id}
            dataLoc={`readonly-tool-card-${tool.id}`}
            description={tool.description}
            name={tool.name}
            type={tool.type as ToolType}
          />
        ))}
        {configurableTools.length === 0 && !hasReadFileTool && (
          <span className="font-inter-normal-13px text-gray-500">
            No tools configured.
          </span>
        )}
      </div>
    </div>
  </div>
);

const SCHEMA_DISPLAY_OPTIONS = {
  showTypeLabels: false,
  showPrimitiveTypeLabels: true,
  showRequired: true,
  showDataPaths: false,
  showPrimitiveDataPath: false,
  autoExpandDepth: 1,
  canCollapse: false,
} as const;

const SchemaSection: React.FC<{
  inputSchema: { [key: string]: unknown } | null | undefined;
  outputSchema: { [key: string]: unknown } | null | undefined;
  hasInputSchema: boolean;
  hasOutputSchema: boolean;
}> = ({ inputSchema, outputSchema, hasInputSchema, hasOutputSchema }) => {
  const [activeTab, setActiveTab] = useState<string>(
    hasInputSchema ? "input" : "output",
  );

  const tabs = [
    {
      key: "input",
      label: "Input",
      content: hasInputSchema ? (
        <JSONSchemaRenderer
          displayOptions={SCHEMA_DISPLAY_OPTIONS}
          schema={inputSchema as unknown as JsonSchema}
        />
      ) : (
        <p className="py-3 font-inter-normal-13px text-gray-500 italic">
          No input schema defined
        </p>
      ),
    },
    {
      key: "output",
      label: "Output",
      content: hasOutputSchema ? (
        <JSONSchemaRenderer
          displayOptions={SCHEMA_DISPLAY_OPTIONS}
          schema={outputSchema as unknown as JsonSchema}
        />
      ) : (
        <p className="py-3 font-inter-normal-13px text-gray-500 italic">
          No output schema defined
        </p>
      ),
    },
  ];

  return (
    <div>
      <h3 className="mb-3 font-inter-semibold-14px text-gray-800">Schema</h3>
      <div className="rounded-lg bg-gray-50 px-5 py-3">
        <Tabs
          panelsClassName="pt-8 pb-2"
          selectedKey={activeTab}
          tabListClassName="border-b border-gray-200"
          tabs={tabs}
          onChange={setActiveTab}
        />
      </div>
    </div>
  );
};
